import React, { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import { searchMatch } from '@/lib/utils';
import { Search, Loader2, ShoppingBag } from 'lucide-react';
import { listingsAPI } from '@/lib/api';
import { useSEO, SEO_KEYWORDS } from '@/hooks/useSEO';
import { useListingGrid } from '@/hooks/useListingGrid';
import ListingCard from '@/components/listings/ListingCard';

const STORAGE_KEY = 'infinite_scroll_all_sales';
const NAV_FLAG_KEY = 'nav_away_all_sales';

// Read saved state synchronously - only if user navigated away (not refresh)
const readSavedState = () => {
    try {
        const navigatedAway = sessionStorage.getItem(NAV_FLAG_KEY);
        sessionStorage.removeItem(NAV_FLAG_KEY);
        
        if (!navigatedAway) {
            sessionStorage.removeItem(STORAGE_KEY);
            return null;
        }
        
        const saved = sessionStorage.getItem(STORAGE_KEY);
        if (saved) {
            const state = JSON.parse(saved);
            if (state && state.sales?.length > 0 && Date.now() - state.timestamp < 10 * 60 * 1000) {
                return state;
            }
        }
    } catch (e) {
        // Ignore
    }
    return null;
};

const AllSalesPage = () => {
    // Check if coming from floating menu - clear cached state
    useEffect(() => {
        const navFlag = sessionStorage.getItem('nav_to_all_sales');
        if (navFlag) {
            sessionStorage.removeItem('nav_to_all_sales');
            sessionStorage.removeItem(STORAGE_KEY);
            sessionStorage.removeItem(NAV_FLAG_KEY);
        }
    }, []);
    
    // Read saved state ONCE at initialization
    const savedStateRef = useRef(readSavedState());
    const initialState = savedStateRef.current;
    const wasRestored = initialState !== null;
    
    const [sales, setSales] = useState(initialState?.sales || []);
    const [loading, setLoading] = useState(!wasRestored);
    const [loadingMore, setLoadingMore] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [currentPage, setCurrentPage] = useState(initialState?.currentPage || 1);
    const [itemsPerPage, setItemsPerPage] = useState(24);
    const [totalItems, setTotalItems] = useState(initialState?.totalItems || 0);
    const [hasMore, setHasMore] = useState(initialState?.hasMore ?? true);
    
    // Grid settings using shared hook
    const { gridSettings, singleColumn } = useListingGrid();
    
    const loadMoreRef = useRef(null);
    const shouldFetchRef = useRef(!wasRestored);
    const scrollRestoredRef = useRef(false);

    // Restore scroll position after mount if we have saved state
    useEffect(() => {
        if (!wasRestored || !initialState?.scrollY || initialState.scrollY <= 0) {
            return;
        }
        
        if (scrollRestoredRef.current) {
            return;
        }
        
        const targetScroll = initialState.scrollY;
        let attempts = 0;
        const maxAttempts = 50;
        
        const attemptScroll = () => {
            attempts++;
            const docHeight = document.documentElement.scrollHeight;
            
            if (docHeight > targetScroll || attempts >= maxAttempts) {
                window.scrollTo({ top: targetScroll, behavior: 'instant' });
                
                setTimeout(() => {
                    const actualScroll = window.scrollY;
                    const diff = Math.abs(actualScroll - targetScroll);
                    
                    if (diff > 100 && attempts < maxAttempts) {
                        requestAnimationFrame(attemptScroll);
                    } else {
                        scrollRestoredRef.current = true;
                    }
                }, 100);
            } else {
                setTimeout(attemptScroll, 100);
            }
        };
        
        const timerId = setTimeout(attemptScroll, 50);
        return () => clearTimeout(timerId);
    }, [wasRestored, initialState?.scrollY, sales.length]);

    // Save state on scroll (debounced) and before navigation
    useEffect(() => {
        if (sales.length === 0) return;
        
        let scrollTimeout;
        const handleScroll = () => {
            clearTimeout(scrollTimeout);
            scrollTimeout = setTimeout(() => {
                const state = {
                    sales,
                    currentPage,
                    hasMore,
                    totalItems,
                    scrollY: window.scrollY,
                    timestamp: Date.now()
                };
                sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
            }, 200);
        };
        
        // Save immediately on mousedown before navigation
        const handleMouseDown = (e) => {
            const link = e.target.closest('a[href^="/listing/"], a[href^="/category/"], a[href^="/user/"]');
            if (link) {
                sessionStorage.setItem(NAV_FLAG_KEY, 'true');
                const state = {
                    sales,
                    currentPage,
                    hasMore,
                    totalItems,
                    scrollY: window.scrollY,
                    timestamp: Date.now()
                };
                sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
            }
        };
        
        window.addEventListener('scroll', handleScroll, { passive: true });
        document.addEventListener('mousedown', handleMouseDown, { capture: true });
        
        return () => {
            window.removeEventListener('scroll', handleScroll);
            document.removeEventListener('mousedown', handleMouseDown, { capture: true });
            clearTimeout(scrollTimeout);
        };
    }, [sales, currentPage, hasMore, totalItems]);

    // SEO
    useSEO({
        title: 'Vânzări Preț Fix - Piese de Colecție',
        description: 'Cumpără piese de colecție la preț fix. Monede, medalii, decorații, uniforme militare și alte piese de numismatică, faleristică disponibile pentru cumpărare imediată.',
        keywords: SEO_KEYWORDS.vanzari + ', ' + SEO_KEYWORDS.base,
        url: 'https://colectarium.ro/all-sales'
    });

    // Breadcrumb schema
    const breadcrumbSchema = {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Acasă", "item": "https://colectarium.ro/" },
            { "@type": "ListItem", "position": 2, "name": "Vânzări", "item": "https://colectarium.ro/all-sales" }
        ]
    };

    // ItemList schema for sales
    const itemListSchema = {
        "@context": "https://schema.org",
        "@type": "ItemList",
        "name": "Vânzări la Preț Fix pe Colectarium",
        "description": "Lista de piese de colecție disponibile pentru cumpărare imediată",
        "numberOfItems": totalItems,
        "itemListElement": sales.slice(0, 10).map((sale, index) => ({
            "@type": "ListItem",
            "position": index + 1,
            "url": `https://colectarium.ro/listing/${sale.slug || sale.listing_id}`,
            "name": sale.title
        }))
    };

    // Fetch sales with infinite scroll
    useEffect(() => {
        // Skip fetch if we restored from saved state and it's the first page
        if (!shouldFetchRef.current && currentPage === 1) {
            return;
        }
        
        const fetchSales = async () => {
            if (currentPage > 1) {
                setLoadingMore(true);
            } else {
                setLoading(true);
            }
            
            try {
                const skip = (currentPage - 1) * itemsPerPage;
                const res = await listingsAPI.getAll({ 
                    listing_type: 'direct_sale', 
                    status: 'active',
                    skip,
                    limit: itemsPerPage
                });
                const data = res.data?.listings || res.data || [];
                const total = res.data?.total || data.length;
                
                if (currentPage > 1) {
                    setSales(prev => [...prev, ...data]);
                } else {
                    setSales(data);
                }
                
                setTotalItems(total);
                setHasMore(skip + data.length < total);
                
                // Mark that we should fetch on subsequent page changes
                shouldFetchRef.current = true;
            } catch (error) {
                console.error('Error fetching sales:', error);
            } finally {
                setLoading(false);
                setLoadingMore(false);
            }
        };
        fetchSales();
    }, [currentPage, itemsPerPage]);

    // Infinite scroll observer
    useEffect(() => {
        if (!hasMore || loading || loadingMore) return;
        
        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting && hasMore && !loadingMore) {
                    setCurrentPage(prev => prev + 1);
                }
            },
            { threshold: 0.1, rootMargin: '100px' }
        );
        
        if (loadMoreRef.current) {
            observer.observe(loadMoreRef.current);
        }
        
        return () => observer.disconnect();
    }, [hasMore, loading, loadingMore]);

    const filteredSales = sales.filter(s => 
        !searchQuery || 
        searchMatch(s.title, searchQuery) ||
        searchMatch(s.category_name, searchQuery)
    );

    if (loading && sales.length === 0) {
        return (
            <div className="container mx-auto py-8 px-4">
                <Skeleton className="h-12 w-64 mb-6" />
                <Skeleton className="h-10 w-full mb-6" />
                <div 
                    className="grid gap-6 category-grid"
                    style={{
                        '--grid-cols-mobile': gridSettings.mobile,
                        '--grid-cols-tablet': gridSettings.tablet,
                        '--grid-cols-desktop': gridSettings.desktop,
                    }}
                >
                    {[...Array(8)].map((_, i) => (
                        <Skeleton key={i} className="h-[340px] rounded-2xl" />
                    ))}
                </div>
            </div>
        );
    }

    return (
        <div className="container mx-auto py-8 px-4">
            {/* SEO Structured Data */}
            <Helmet>
                <script type="application/ld+json">
                    {JSON.stringify(breadcrumbSchema)}
                </script>
                <script type="application/ld+json">
                    {JSON.stringify(itemListSchema)}
                </script>
            </Helmet>
            
            <div className="flex items-center justify-between flex-wrap gap-4 mb-6">
                <div>
                    <h1 className="text-3xl font-bold flex items-center gap-3">
                        <ShoppingBag className="w-8 h-8 text-green-500" />
                        Toate vânzările
                    </h1>
                    <p className="text-muted-foreground mt-1">
                        {totalItems} vânzări disponibile
                    </p>
                </div>
                <div className="relative w-full sm:w-80">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                        placeholder="Caută vânzări..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="pl-10 rounded-full"
                    />
                </div>
            </div>

            {filteredSales.length === 0 && !loading ? (
                <div className="text-center py-16 text-muted-foreground">
                    <ShoppingBag className="w-16 h-16 mx-auto mb-4 opacity-30" />
                    <p className="text-lg">Nu există vânzări disponibile momentan</p>
                </div>
            ) : (
                <>
                    <div 
                        className="grid gap-6 category-grid"
                        style={{
                            '--grid-cols-mobile': gridSettings.mobile,
                            '--grid-cols-tablet': gridSettings.tablet,
                            '--grid-cols-desktop': gridSettings.desktop,
                        }}
                    >
                        {filteredSales.map((sale) => (
                            <ListingCard 
                                key={sale.listing_id} 
                                listing={sale}
                                singleColumn={singleColumn}
                            />
                        ))}
                    </div>
                    
                    {/* Infinite scroll loader */}
                    <div 
                        ref={loadMoreRef} 
                        className="flex justify-center py-8"
                        data-testid="infinite-scroll-trigger"
                    >
                        {loadingMore && (
                            <div className="flex items-center gap-2 text-muted-foreground">
                                <Loader2 className="w-5 h-5 animate-spin" />
                                <span>Se încarcă...</span>
                            </div>
                        )}
                        {!hasMore && sales.length > 0 && (
                            <p className="text-muted-foreground text-sm">
                                Ai văzut toate cele {totalItems} vânzări
                            </p>
                        )}
                    </div>
                </>
            )}
        </div>
    );
};

export default AllSalesPage;
